import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { useApi } from '@/lib/auth'
import { usePreviewData } from '../preview-data'
import { adminRolesQueryKey } from './use-admin-roles'

const userSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  imageUrl: z.string(),
  role: z.string(),
  joinedAt: z.number(),
})

// GET /v1/admin/users -> { success, data: AdminUser[] }
const usersEnvelopeSchema = z.object({ success: z.boolean(), data: z.array(userSchema) })
const okSchema = z.object({ success: z.boolean() }).passthrough()

export type AdminUser = z.infer<typeof userSchema>

export const adminUsersQueryKey = ['admin', 'users'] as const

/** Org members with their role id. Pair with `useAdminRoles()` for the role select options. */
export function useAdminUsers() {
  const api = useApi()
  const preview = usePreviewData()
  return useQuery({
    queryKey: preview ? ['preview', 'admin-users'] : adminUsersQueryKey,
    queryFn: async (): Promise<AdminUser[]> => {
      const res = await api.get('/v1/admin/users', usersEnvelopeSchema)
      if (!res.success) throw new Error('Failed to fetch users')
      return res.data
    },
    enabled: !preview,
    ...(preview ? { initialData: preview.adminUsers, staleTime: Number.POSITIVE_INFINITY } : {}),
  })
}

/** Assign a role. `mutate({ userId, role })` PATCH /v1/admin/users/:userId/role. Invalidates users + roles (user counts). */
export function useAssignUserRole() {
  const api = useApi()
  const qc = useQueryClient()
  const preview = usePreviewData()
  return useMutation({
    mutationFn: async (v: { userId: string; role: string }) => {
      if (preview) return { success: true }
      return api.request(
        `/v1/admin/users/${encodeURIComponent(v.userId)}/role`,
        { method: 'PATCH', body: { role: v.role } },
        okSchema,
      )
    },
    onSuccess: async () => {
      if (preview) return
      await Promise.all([
        qc.invalidateQueries({ queryKey: adminUsersQueryKey }),
        qc.invalidateQueries({ queryKey: adminRolesQueryKey }),
      ])
    },
  })
}
